import { downloadZip } from 'client-zip';
import { api } from '../api';
import { triggerBrowserDownload } from './downloadFile';

const FETCH_CONCURRENCY = 5;

/**
 * Network attempts per file before its download fails (1 initial + 2 retries,
 * exponential backoff from RETRY_BASE_DELAY_MS).
 *
 * A transient browser→S3 failure surfaces as a bare `TypeError` with no HTTP
 * status, either during the request ("Failed to fetch") or while the body
 * streams ("network error"). Prod saw exactly one in ~27k GETs during a 2 h,
 * 19-part download (Sentry WEBAPP-FRONTEND-2, 2026-08-24) and, with no retry,
 * it failed the whole last part. The body is buffered inside the retry loop so
 * both phases are covered — at the cost of holding up to FETCH_CONCURRENCY
 * complete bodies in memory instead of streaming one.
 */
export const FETCH_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 750;
/** Statuses worth another attempt: throttling and transient server errors. */
const RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);

/**
 * A file could not be downloaded from S3.
 *
 * The message is deliberately fixed: Sentry titles and groups issues by it, so
 * interpolating the varying reason would split one incident class across several
 * issues. The reason travels in `detail`.
 *
 * `relativePath` is a tenant's file path and is kept out of the error message
 * and out of the event payload — the report sends `fileId` instead. Note this is
 * not a guarantee that the path never reaches Sentry: replays are unmasked
 * (`maskAllText: false`) and sampled at 100% on error, so an error-triggered
 * replay can still capture the modal that renders it. Masking that is a separate
 * change (branch `fix/sentry-replay-masking`).
 */
export class FileFetchError extends Error {
  readonly status: number | null;
  readonly fileId: string;
  readonly relativePath: string;
  readonly detail: string;
  constructor(detail: string, opts: { status?: number | null; fileId: string; relativePath: string }) {
    super('Failed to download a file from storage');
    this.name = 'FileFetchError';
    this.detail = detail;
    this.status = opts.status ?? null;
    this.fileId = opts.fileId;
    this.relativePath = opts.relativePath;
  }
}

/** The folder holds no completed files — a benign outcome, not an error to alert on. */
export class FolderEmptyError extends Error {
  constructor() {
    super('Folder is empty');
    this.name = 'FolderEmptyError';
  }
}

/**
 * Resolve after `ms`, or as soon as `signal` aborts — so a cancelled download
 * stops within a tick instead of sitting out the remaining backoff, and the
 * timer is always cleared.
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) return resolve();
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener('abort', done, { once: true });
  });
}

/**
 * Files per zip when a folder is too large for a single download. Must equal the
 * backend's MAX_MANIFEST_PAGE_FILES (lambda/admin-api/handlers/downloads.ts).
 *
 * Sized to keep each manifest response under API Gateway's 6 MB Lambda-proxy
 * cap. Each entry is ~2 KB — larger than it looks because the Lambda presigns
 * with temporary IAM-role credentials, so every URL carries a ~1 KB
 * `X-Amz-Security-Token`. At 1,500 entries that's ~3 MB in prod (measured
 * 2026-08-24: 3,057,761 bytes) and stays under 6 MB even for pathologically long
 * file names/paths; 5,000 produced ~8 MB and the gateway returned a 502 with no
 * CORS headers. The parts loop steps by the
 * actual returned count, so a smaller server cap degrades gracefully (more,
 * smaller parts) rather than skipping files.
 */
export const DOWNLOAD_PAGE_SIZE = 1500;

export interface FolderDownloadProgress {
  completed: number;
  total: number;
  currentFile: string | null;
}

export interface FolderDownloadOptions {
  onProgress?: (progress: FolderDownloadProgress) => void;
  signal?: AbortSignal;
  /** Test seam — defaults to the browser's fetch. */
  fetchImpl?: typeof fetch;
  /** Test seam — defaults to a real timer. */
  sleepImpl?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

export interface ManifestFile {
  id: string;
  relativePath: string;
  url: string;
  size: number;
  contentType: string;
}

/**
 * Fetch one file into a Blob, retrying transient failures in either phase.
 * A non-retryable HTTP status throws immediately.
 */
async function fetchManifestFile(
  file: ManifestFile,
  signal: AbortSignal,
  fetchImpl: typeof fetch,
  sleepImpl: (ms: number, signal?: AbortSignal) => Promise<void>
): Promise<Blob> {
  const fail = (detail: string, status: number | null = null) =>
    new FileFetchError(detail, { status, fileId: file.id, relativePath: file.relativePath });
  let lastFailure = '';
  // Remembered so an exhausted retry still reports the status it kept seeing,
  // rather than discarding it as an unknown failure.
  let lastStatus: number | null = null;

  for (let attempt = 1; attempt <= FETCH_ATTEMPTS; attempt++) {
    signal.throwIfAborted();

    let response: Response | undefined;
    try {
      lastStatus = null;
      response = await fetchImpl(file.url, { signal });
      if (response.ok) {
        // Buffer the body here so a mid-body transport failure is retried like a
        // request-phase one. Handing client-zip a Response instead would surface
        // that failure inside client-zip, past this retry — and its iterator
        // wrapper has no `return()`, so the generator below would stay parked at
        // its `yield` with the sibling fetches still streaming.
        return await response.blob();
      }
    } catch (err) {
      // fetch()/blob() reject with a TypeError on transport failures — no status
      // to inspect, so treat it as transient. Aborts propagate untouched.
      signal.throwIfAborted();
      // Release the body before dropping the reference: on a blob() failure this
      // is the only chance to close the stream.
      if (response) void response.body?.cancel().catch(() => undefined);
      response = undefined;
      lastFailure = err instanceof Error ? err.message : String(err);
      lastStatus = null; // a transport failure has no status
    }

    if (response) {
      void response.body?.cancel().catch(() => undefined);
      if (!RETRYABLE_STATUSES.has(response.status)) {
        throw fail(`HTTP ${response.status}`, response.status);
      }
      lastFailure = `HTTP ${response.status}`;
      lastStatus = response.status;
    }

    if (attempt < FETCH_ATTEMPTS) {
      await sleepImpl(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal);
    }
  }

  throw fail(`${FETCH_ATTEMPTS} attempts, last: ${lastFailure}`, lastStatus);
}

type SettledFetch =
  | { ok: true; file: ManifestFile; blob: Blob }
  | { ok: false; error: unknown };

/**
 * Yields one zip entry per file, keeping FETCH_CONCURRENCY fetches in flight and
 * preserving manifest order. `controller` is this zip's own controller: it is
 * aborted as soon as any fetch fails for good, so the others stop instead of
 * streaming into a zip nobody will finish. Exported for tests.
 */
export async function* zipEntries(
  files: ManifestFile[],
  controller: AbortController,
  onProgress?: (progress: FolderDownloadProgress) => void,
  fetchImpl: typeof fetch = fetch,
  sleepImpl: (ms: number, signal?: AbortSignal) => Promise<void> = sleep
): AsyncGenerator<{ name: string; input: Blob; lastModified: Date }> {
  const { signal } = controller;
  const total = files.length;
  let completed = 0;
  const pending: Array<Promise<SettledFetch>> = [];
  let idx = 0;
  let hasFatal = false;
  let fatal: unknown;

  const startNext = () => {
    if (hasFatal || idx >= files.length) return;
    const file = files[idx++];
    // Settle rather than reject. A queued fetch can fail while an earlier entry
    // is still being zipped, and a rejected promise nobody has awaited yet fires
    // `unhandledrejection` — which is how WEBAPP-FRONTEND-2 reached Sentry as an
    // "unhandled" crash even though the UI had caught it.
    pending.push(
      fetchManifestFile(file, signal, fetchImpl, sleepImpl).then(
        (blob) => ({ ok: true as const, file, blob }),
        (error) => {
          // Stop the whole zip on the first terminal failure rather than when
          // this entry reaches the head of the queue, and remember the real
          // cause so the siblings' AbortErrors can't mask it.
          if (!hasFatal && !signal.aborted) {
            hasFatal = true;
            fatal = error;
            controller.abort();
          }
          return { ok: false as const, error };
        }
      )
    );
  };

  for (let i = 0; i < Math.min(FETCH_CONCURRENCY, files.length); i++) {
    startNext();
  }

  try {
    while (pending.length > 0) {
      const next = pending.shift();
      if (!next) break;
      const settled = await next;
      if (hasFatal) throw fatal;
      if (!settled.ok) throw settled.error;
      // A cancel that lands while the last bodies are buffered must not still
      // write the zip to disk.
      signal.throwIfAborted();
      onProgress?.({ completed, total, currentFile: settled.file.relativePath });
      yield {
        name: settled.file.relativePath,
        input: settled.blob,
        lastModified: new Date(),
      };
      completed++;
      onProgress?.({ completed, total, currentFile: null });
      startNext();
    }
  } catch (err) {
    controller.abort();
    throw err;
  }
}

/**
 * Fetch every file in `files` (concurrently), stream them into a single zip, and
 * trigger a browser download named `zipName`. The whole zip is materialized in
 * memory, so callers keep each call bounded to one page of files.
 */
export async function zipManifestFiles(
  files: ManifestFile[],
  zipName: string,
  options: FolderDownloadOptions = {}
): Promise<void> {
  const { onProgress, signal: externalSignal, fetchImpl = fetch, sleepImpl = sleep } = options;

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    onProgress?.({ completed: 0, total: files.length, currentFile: null });

    const zipResponse = downloadZip(zipEntries(files, controller, onProgress, fetchImpl, sleepImpl));

    let blob: Blob;
    try {
      blob = await zipResponse.blob();
    } catch (err) {
      // client-zip's iterator wrapper has no `return()`: if it fails while our
      // generator is parked at a yield, nothing else would stop the in-flight
      // fetches.
      controller.abort();
      throw err;
    }
    const blobUrl = URL.createObjectURL(blob);
    try {
      triggerBrowserDownload(blobUrl, zipName);
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }
  } finally {
    if (externalSignal) externalSignal.removeEventListener('abort', onAbort);
    // Nothing may outlive this zip.
    controller.abort();
  }
}

/**
 * Thrown by {@link downloadFolderAsZip} when a folder spans more than one
 * download page and must instead be fetched as several parts via
 * {@link downloadFolderInParts}. Carries the folder's total file count.
 */
export class FolderTooLargeError extends Error {
  readonly total: number;
  constructor(total: number) {
    super(`Folder has ${total} files — too many for a single zip`);
    this.name = 'FolderTooLargeError';
    this.total = total;
  }
}

/**
 * Download a whole folder as a single zip. Throws {@link FolderTooLargeError}
 * when the folder spans more than one page (the caller should then confirm and
 * call {@link downloadFolderInParts}).
 */
export async function downloadFolderAsZip(
  orgId: string,
  folderId: string,
  options: FolderDownloadOptions = {}
): Promise<void> {
  const page = await api.getFolderDownloadManifest(orgId, folderId, {
    offset: 0,
    limit: DOWNLOAD_PAGE_SIZE,
  });

  if (page.total === 0) {
    throw new FolderEmptyError();
  }
  if (page.total > page.files.length) {
    // More files than fit in one page — switch to a chunked parts download.
    throw new FolderTooLargeError(page.total);
  }

  await zipManifestFiles(page.files, `${page.folderName}.zip`, options);
}

/** Which part a failure happened on — diagnostic context for the report. */
export interface FolderPartLocation {
  offset: number;
  part: number;
  totalParts: number;
}

/**
 * Wraps a part's failure with its location: how far a long run got, for the
 * Sentry event and for the modal — and the point a continued run starts from,
 * so a failure on part 19 of 19 costs one part rather than the whole folder.
 * Inspect `cause` for the kind.
 */
export class FolderPartsError extends Error {
  readonly at: FolderPartLocation;
  readonly cause: unknown;
  constructor(cause: unknown, at: FolderPartLocation) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'FolderPartsError';
    this.cause = cause;
    this.at = at;
  }
}

export interface FolderPartsProgress {
  totalParts: number;
  /** Parts fully completed so far. */
  completedParts: number;
  /** 1-based index of the part currently downloading, or null when finished. */
  currentPart: number | null;
  /** Per-file progress within the current part's zip. */
  fileProgress: FolderDownloadProgress | null;
}

/**
 * Download a too-large folder as several evenly-sized zips ("part k of N"),
 * paging through the folder's full recursive file list. Every file is covered
 * regardless of how it's distributed across subfolders, and each file's path is
 * preserved inside its zip. Parts run sequentially, so peak memory stays bounded
 * to one page's zip. `fetchPage`/`zipFiles` are injectable for tests.
 */
export async function downloadFolderInParts(
  orgId: string,
  folderId: string,
  folderName: string,
  total: number,
  options: {
    onProgress?: (progress: FolderPartsProgress) => void;
    signal?: AbortSignal;
    /** Start again from a part that failed (from {@link FolderPartsError.at}). */
    resumeFrom?: FolderPartLocation;
    fetchPage?: (offset: number, limit: number) => Promise<{ files: ManifestFile[] }>;
    zipFiles?: typeof zipManifestFiles;
    /** Test seams, forwarded to each part's zip so the retry path is reachable here. */
    fetchImpl?: typeof fetch;
    sleepImpl?: (ms: number, signal?: AbortSignal) => Promise<void>;
  } = {}
): Promise<void> {
  const {
    onProgress,
    signal,
    resumeFrom,
    fetchPage = (offset, limit) => api.getFolderDownloadManifest(orgId, folderId, { offset, limit }),
    zipFiles = zipManifestFiles,
    fetchImpl,
    sleepImpl,
  } = options;

  // A continued run picks up at the failed part and keeps the numbering the
  // original run settled on — its first page may be a short final part, which
  // would otherwise skew the recomputed "of N".
  let offset = resumeFrom?.offset ?? 0;
  let part = resumeFrom ? resumeFrom.part - 1 : 0;
  // Determined from the first page's actual size so the "of N" label is correct
  // even if the server's page size differs from DOWNLOAD_PAGE_SIZE.
  let totalParts = resumeFrom?.totalParts ?? 0;

  while (offset < total) {
    if (signal?.aborted) break;

    const pageOffset = offset;
    const currentPart = part + 1;

    let page: { files: ManifestFile[] };
    try {
      page = await fetchPage(pageOffset, DOWNLOAD_PAGE_SIZE);
    } catch (err) {
      if (signal?.aborted) throw err;
      // Carry the location here too, so a manifest failure is continuable.
      throw new FolderPartsError(err, { offset: pageOffset, part: currentPart, totalParts });
    }
    if (page.files.length === 0) break; // safety: nothing left (e.g. concurrent deletion)

    if (totalParts === 0) {
      totalParts = Math.max(1, Math.ceil(total / page.files.length));
    }

    part = currentPart;
    try {
      await zipFiles(page.files, `${folderName} (part ${currentPart} of ${totalParts}).zip`, {
        signal,
        fetchImpl,
        sleepImpl,
        onProgress: (fileProgress) =>
          onProgress?.({
            totalParts,
            completedParts: currentPart - 1,
            currentPart,
            fileProgress,
          }),
      });
    } catch (err) {
      if (signal?.aborted) throw err;
      throw new FolderPartsError(err, { offset: pageOffset, part: currentPart, totalParts });
    }

    offset += page.files.length;
  }

  onProgress?.({
    totalParts: totalParts || 1,
    completedParts: totalParts || 1,
    currentPart: null,
    fileProgress: null,
  });
}
